import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, Loader2, QrCode, RefreshCw } from 'lucide-react';
import jsQR from 'jsqr';
import AppTopHeader from '../components/AppTopHeader';

const extractActivationTarget = (rawValue) => {
  const value = `${rawValue || ''}`.trim();
  if (!value) return null;

  try {
    const url = new URL(value);
    const pathname = url.pathname.replace(/\/+$/, '');

    const qrMatch = pathname.match(/^\/q\/([^/]+)$/i);
    if (qrMatch) {
      return `/q/${qrMatch[1].toUpperCase()}`;
    }

    const legacyMatch = pathname.match(/^\/scan\/([^/]+)$/i);
    if (legacyMatch) {
      return `/scan/${legacyMatch[1]}`;
    }
  } catch (error) {
    // Not a full URL, fall through to direct QR id parsing.
  }

  if (/^[A-Za-z0-9_-]{4,}$/.test(value)) {
    return `/q/${value.toUpperCase()}`;
  }

  return null;
};

const ActivateQRPage = () => {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const detectorRef = useRef(null);
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const scanningRef = useRef(false);
  const useJsQrFallbackRef = useRef(false);

  const [isStarting, setIsStarting] = useState(true);
  const [error, setError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);

  const stopCamera = () => {
    if (frameRef.current) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    scanningRef.current = false;
    setCameraReady(false);
  };

  const handleDetectedValue = (rawValue) => {
    const target = extractActivationTarget(rawValue);

    if (!target) {
      setError('This QR code is not recognized as a SIDEO activation QR. Please scan the sticker QR.');
      return;
    }

    stopCamera();
    navigate(target);
  };

  const scanFrame = async () => {
    if (!videoRef.current || !scanningRef.current) {
      return;
    }

    try {
      if (videoRef.current.readyState >= 2) {
        if (detectorRef.current && !useJsQrFallbackRef.current) {
          const barcodes = await detectorRef.current.detect(videoRef.current);
          const detected = barcodes.find((item) => item.rawValue);

          if (detected?.rawValue) {
            handleDetectedValue(detected.rawValue);
            return;
          }
        } else {
          if (!canvasRef.current) {
            canvasRef.current = document.createElement('canvas');
          }

          const canvas = canvasRef.current;
          const width = videoRef.current.videoWidth;
          const height = videoRef.current.videoHeight;

          if (width > 0 && height > 0) {
            canvas.width = width;
            canvas.height = height;

            const context = canvas.getContext('2d', { willReadFrequently: true });
            if (context) {
              context.drawImage(videoRef.current, 0, 0, width, height);
              const imageData = context.getImageData(0, 0, width, height);
              const code = jsQR(imageData.data, width, height, {
                inversionAttempts: 'attemptBoth',
              });

              if (code?.data) {
                handleDetectedValue(code.data);
                return;
              }
            }
          }
        }
      }
    } catch (scanError) {
      console.error('QR scan failed:', scanError);
    }

    frameRef.current = requestAnimationFrame(scanFrame);
  };

  const startCamera = async () => {
    stopCamera();
    setError('');
    setIsStarting(true);
    useJsQrFallbackRef.current = false;

    if (!window.isSecureContext) {
      setError('Camera access requires a secure HTTPS connection.');
      setIsStarting(false);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Camera access is not supported on this browser. Please use your phone camera to scan the sticker directly.');
      setIsStarting(false);
      return;
    }

    try {
      if ('BarcodeDetector' in window) {
        detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
      } else {
        detectorRef.current = null;
        useJsQrFallbackRef.current = true;
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }

      scanningRef.current = true;
      setCameraReady(true);
      setIsStarting(false);
      frameRef.current = requestAnimationFrame(scanFrame);
    } catch (cameraError) {
      console.error('Camera start failed:', cameraError);
      setError('Unable to access camera. Please allow camera permission and try again, or use your phone camera directly on the sticker.');
      setIsStarting(false);
    }
  };

  useEffect(() => {
    startCamera();

    return () => {
      stopCamera();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <AppTopHeader backToHome backLabel="Back to Home" />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8 border-b border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-blue-800 flex items-center justify-center shadow-sm">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Barlow' }}>
                  Activate Your QR Tag
                </h1>
              </div>
            </div>
            <p className="text-slate-600">
              Point your camera at the QR sticker. Once scanned, we’ll take you straight to the activation flow.
            </p>
          </div>

          <div className="p-6 sm:p-8">
            <div className="rounded-3xl bg-slate-950 overflow-hidden relative aspect-[3/4] sm:aspect-video border border-slate-200">
              <video
                ref={videoRef}
                className="w-full h-full object-cover"
                playsInline
                muted
                autoPlay
              />

              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="w-56 h-56 max-w-[70vw] max-h-[70vw] border-2 border-white/80 rounded-3xl shadow-[0_0_0_9999px_rgba(15,23,42,0.45)]" />
              </div>

            </div>

            <div className="mt-5 space-y-3">
              <div className="flex items-start gap-3 text-slate-700 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
                <QrCode className="w-5 h-5 mt-0.5 text-blue-700" />
                <div>
                  <p className="font-semibold">Scan the QR Tag</p>
                  <p className="text-sm text-slate-600">Hold your phone 15–20 cm away and keep the code inside the box.</p>
                </div>
              </div>

              {isStarting && (
                <div className="flex items-center gap-2 text-slate-600 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opening your camera...
                </div>
              )}

              {!isStarting && cameraReady && !error && (
                <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3">
                  Camera is ready. Scan your sticker to continue.
                </div>
              )}

              {error && (
                <div className="space-y-3">
                  <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3">
                    {error}
                  </div>
                  <button
                    onClick={startCamera}
                    className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Try Camera Again
                  </button>
                </div>
              )}

              <p className="text-xs text-slate-500">
                Tip: If your browser blocks the camera, you can still activate by scanning the sticker with your phone’s native camera app.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ActivateQRPage;
